import docendo from '../../docendo'
import Vue from 'vue'
import eventbus from '../../eventbus'

function isCostCenterArchived (costCenter) {
  return !!(costCenter && (costCenter.archived === true || costCenter.archived_at))
}

function decorateCostCenters (costCenters) {
  return costCenters
    .slice()
    .sort((a, b) => {
      const aName = a.name || ''
      const bName = b.name || ''
      return aName.localeCompare(bName, 'da-DK')
    })
    .map(cc => ({
      ...cc,
      title: cc.name
    }))
}

function upsertCostCenter (state, costCenter) {
  const idx = state.costCenters.findIndex(cc => cc.id === costCenter.id)
  if (idx > -1) {
    Vue.set(state.costCenters, idx, costCenter)
  } else {
    state.costCenters.push(costCenter)
  }
}

const state = {
  isLoadingEmploymentsTypesForSchool: false,
  employmentsTypes: [],
  employments: [],
  isSubmitting: false,
  isSubmittingUser: false,
  salaryCodes: [],
  isLoadingSalaryCodes: false,
  employmentTemplates: [],
  costCenters: [], // [name: 'Vest', code: 1]
  isLoadingCostCenters: false,
  isUpdatingCostCenters: false,
  absenceCodes: [],
  isLoadingAbsenceCodes: false
}

const getters = {
  absenceCodesSorted: state => {
    return state.absenceCodes
  },
  costCenters: state => {
    return decorateCostCenters(state.costCenters.filter(cc => !isCostCenterArchived(cc)))
  },
  costCentersArchived: state => {
    return decorateCostCenters(state.costCenters.filter(cc => isCostCenterArchived(cc)))
  },
  costCentersAll: state => {
    return decorateCostCenters(state.costCenters)
  },
  employmentTemplatesSorted (state) {
    return state.employmentTemplates.slice().sort((a, b) => {
      // Først: manual_salary (false før true)
      if (a.manual_salary !== b.manual_salary) {
        return a.manual_salary ? 1 : -1
      }
      // Derefter: title alfabetisk
      return a.title.localeCompare(b.title, 'da-DK')
    })
  },
  schoolIdLocal (state, getters, rootState, rootGetters) {
    return rootGetters.schoolId
  },
  salaryCodesSorted (state, getters) {
    // Lønart hedder den ikke hos SD der er det tillægskode?
    // Visningen bevares (vTitle = "lønart - navn"), men der sorteres på lønkodens titel (navn),
    // med selve lønarten som sekundær sortering ved ens navne.
    // vSupplementTitle bruges i tillægs-/opgaveflowet: hos SD vises altid "tillægsnummer-lønart navn"
    // (fx "111122-833 UV-tillæg 751 - lærer (ovk. m. pension)"), med fallback hvis et af tallene mangler.
    const isSd = getters.salarySystem === 'SD'
    return state.salaryCodes.map(salaryCode => {
      const name = salaryCode.name || ''
      let vSupplementTitle
      if (isSd) {
        const hasTillaegsnummer = salaryCode.tillaegsnummer != null && salaryCode.tillaegsnummer !== ''
        const hasLoenart = salaryCode.loenart != null && salaryCode.loenart !== ''
        let prefix
        if (hasTillaegsnummer && hasLoenart) {
          prefix = salaryCode.tillaegsnummer + '-' + salaryCode.loenart
        } else if (hasTillaegsnummer) {
          prefix = String(salaryCode.tillaegsnummer)
        } else {
          prefix = String(salaryCode.loenart)
        }
        vSupplementTitle = prefix + (name ? ' ' + name : '')
      } else {
        vSupplementTitle = salaryCode.loenart + ' - ' + name
      }
      return {
        ...salaryCode,
        code: salaryCode.loenart,
        vTitle: salaryCode.loenart + ' - ' + name,
        vSupplementTitle
      }
    }).sort((a, b) => {
      const byName = (a.name || '').localeCompare(b.name || '', 'da-DK', {sensitivity: 'base'})
      if (byName !== 0) return byName
      return String(a.loenart || '').localeCompare(String(b.loenart || ''), 'da-DK', {numeric: true})
    })
  },
  employmentTypesActive: (state) => {
    // Create a shallow copy of the array before filtering and sorting
    return [...state.employmentsTypes].sort((a, b) => a.localeCompare(b))
  },
  employmentToSelectForRules: (state) => {
    const addArr = ['*Stillingen fra lektionen før*', '*Stillingen fra lektionen efter*']

    // Start med de eksisterende typer
    const sortedEmployments = state.employmentsTypes.slice().sort((a, b) => a.localeCompare(b))

    // Find titler fra templates, der ikke allerede findes
    const templateTitles = state.employmentTemplates
      .map(t => t.title)
      .filter(title => !sortedEmployments.includes(title))

    // Kombinér alt og sortér alfabetisk (men bevar addArr til sidst)
    const combined = [...sortedEmployments, ...templateTitles].sort((a, b) => a.localeCompare(b))

    return [...combined, ...addArr]
  },
  employmentTitles: (state) => {
    let uniqueTitles = []
    state.employments.forEach(employment => {
      let idx = uniqueTitles.findIndex(uniqueTitle => uniqueTitle === employment.title)
      if (idx < 0) {
        uniqueTitles.push(employment.title)
      }
    })
    return uniqueTitles
  },
  allSalaryCodes: (state) => {
    let uniqueSalaryCodes = []
    state.employmentsTypes.forEach(employmentType => {
      employmentType.salary_codes.forEach(salaryCode => {
        let idx = uniqueSalaryCodes.findIndex(uniqueSalaryCode => uniqueSalaryCode.code === salaryCode.code)
        if (idx < 0) {
          uniqueSalaryCodes.push(salaryCode)
        }
      })
    })
    return uniqueSalaryCodes.sort((a, b) => a.title.localeCompare(b.title))
  },
  employmentsInEmploymentTypeHash (state, data) {
    let hash = {}
    state.employments.forEach(employment => {
      if (employment.employment_type_id) {
        if (hash[employment.employment_type_id]) {
          hash[employment.employment_type_id].push(employment.user_id)
        } else {
          hash[employment.employment_type_id] = [employment.user_id]
        }
      }
    })
    return hash
  },
  employmentsInUserIdHash (state, getters) {
    let hash = {}
    state.employments.forEach(employment => {
      if (hash[employment.user_id]) {
        hash[employment.user_id].push(employment)
      } else {
        hash[employment.user_id] = [employment]
      }
    })
    return hash
  }
}

const mutations = {
  isLoadingEmploymentsTypesForSchool (state, boolean) {
    state.isLoadingEmploymentsTypesForSchool = boolean
  },
  setEmployments (state, data) {
    state.employments = data
  },
  setEmploymentsTypes (state, data) {
    state.employmentsTypes = data
  },
  updateEmploymentsType (state, obj) {
    let idx = state.employmentsTypes.findIndex(type => type.id === obj.id)
    Vue.set(state.employmentsTypes, idx, obj)
  },
  addEmploymentsType (state, obj) {
    state.employmentsTypes.push(obj)
  },
  deleteEmploymentsType (state, id) {
    let idx = state.employmentsTypes.findIndex(type => type.id === id)
    state.employmentsTypes.splice(idx, 1)
  },
  setAbsenceCodes (state, data) {
    state.absenceCodes = data
  }
}

const actions = {
  readAbsenceCodes: (context) => {
    const schoolId = context.getters.schoolIdLocal
    context.state.isLoadingAbsenceCodes = true
    docendo.listSilkeborgDataAbsenceCodes(schoolId, success => {
      context.commit('setAbsenceCodes', success)
      context.state.isLoadingAbsenceCodes = false
    }, failure => {
      console.log('failed read absence codes')
    })
  },
  readEmploymentTemplates: (context, data) => {
    const schoolId = context.getters.schoolIdLocal
    docendo.employmentTemplatesForSchool(schoolId, success => {
      context.state.employmentTemplates = success
    }, failure => {
      console.log('failed!')
    })
  },
  readSalaryCodes: context => {
    context.state.isLoadingSalaryCodes = true
    const schoolId = context.getters.schoolIdLocal
    docendo.salaryCodes(schoolId, success => {
      context.state.salaryCodes = success
      context.state.isLoadingSalaryCodes = false
    })
  },
  readEmploymentTypes: (context) => {
    const schoolId = context.rootGetters.schoolId
    context.commit('isLoadingEmploymentsTypesForSchool', true)
    docendo.employmentTypesForSchool(schoolId, data => {
      context.commit('setEmploymentsTypes', data)
      context.commit('isLoadingEmploymentsTypesForSchool', false)
    }, failure => {
      console.log('failed read subjects')
    })
  },
  createEmploymentType: (context, obj) => {
    const schoolId = context.rootGetters.schoolId
    docendo.createEmploymentType(schoolId, obj.title, obj.manualSalary, success => {
      context.commit('addEmploymentsType', success)
    }, failure => {
      console.log('failure')
    })
  },
  updateEmploymentsType: (context, obj) => {
    const schoolId = context.rootGetters.schoolId
    docendo.updateEmploymentType(schoolId, obj.id, obj.title, obj.manualSalary, success => {
      context.commit('updateEmploymentsType', success)
    }, failure => {
      console.log('failure')
    })
  },
  readEmployments: (context) => {
    const schoolId = context.rootGetters.schoolId
    context.commit('isLoadingEmploymentsTypesForSchool', true)
    docendo.employmentsForSchool(schoolId, data => {
      context.commit('setEmployments', data)
      context.commit('isLoadingEmploymentsTypesForSchool', false)
    }, failure => {
      console.log('failed read subjects')
    })
  },
  createSalaryCode: (context, obj) => {
    context.state.isSubmitting = true
    docendo.createSalaryCode(obj.schoolId, obj.employmentTypeId, obj.code, obj.title, data => {
      console.log('createSalaryCode::oprettet', data)
      context.state.isSubmitting = false
      context.dispatch('readEmploymentTypes')
      context.dispatch('readEmployments')
    }, failure => {
      console.log('failed read subjects')
    })
  },
  createEmployments: (context, obj) => {
    context.state.isCreatingUserEmployments = true
    console.log('vuex::createEmployments', obj)
    docendo.bulkCreateEmployment(obj.schoolId, obj.userIds, obj.employmentTypeIds, obj.isManualSalary, data => {
      context.state.isCreatingUserEmployments = false
      context.dispatch('readEmploymentTypes')
      context.dispatch('readEmployments')
      eventbus.toast = {text: 'Oprettet', 'success': true}
    }, failure => {
      eventbus.toast = {text: failure.error, 'success': false}
      console.log('failed read subjects')
    })
  },
  deleteEmployment: (context, obj) => {
    docendo.deleteEmployment(obj.schoolId, obj.userId, obj.employmentId, data => {
      if (data.status === 'error') {
        eventbus.toast = {text: data.message, 'success': false}
      }
      context.dispatch('readEmployments')
    }, failure => {
      eventbus.toast = {text: failure.error, 'success': false}
      console.log('failed read subjects')
    })
  },
  archiveEmployment: (context, obj) => {

  },
  updateEmployment: (context, obj) => {
    return new Promise((resolve, reject) => {
      docendo.updateEmployment(
        obj.schoolId,
        obj.userId,
        obj.employmentId,
        obj.manualSalary,
        obj.defaultLoenart,
        obj.defaultLoebenummer,
        data => {
          context.dispatch('readEmployments').then(() => {
            resolve() // Fortæller at opdateringen er færdig
          })
        },
        failure => {
          eventbus.toast = {text: failure.error, success: false}
          console.log('failed to update employment')
          reject(failure.error) // Fortæller at der var en fejl
        }
      )
    })
  },
  readCostCenters: (context) => {
    const schoolId = context.getters.schoolIdLocal
    context.state.isLoadingCostCenters = true
    docendo.listSalaryCostCenter(schoolId, success => {
      context.state.costCenters = success
      context.state.isLoadingCostCenters = false
    }, failure => {
      console.log('failed read subjects')
    })
  },
  createCostCenter: (context, costCenter) => {
    const schoolId = context.getters.schoolIdLocal
    context.state.isUpdatingCostCenters = true
    docendo.createSalaryCostCenter(schoolId, costCenter.name, costCenter.code, success => {
      context.state.costCenters.push(success)
      context.state.isUpdatingCostCenters = false
    }, failure => {
      console.log('failed read subjects')
    })
  },
  archiveCostCenter: (context, costCenter) => {
    return new Promise((resolve, reject) => {
      const schoolId = context.getters.schoolIdLocal
      docendo.archiveSalaryCostCenter(schoolId, costCenter.id, success => {
        upsertCostCenter(context.state, success)
        resolve(success)
      }, failure => {
        eventbus.toast = {text: (failure && failure.error) || 'Kunne ikke arkivere omkostningssted', success: false}
        reject(failure)
      })
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/employments.js